/*
 * Prevent collapsed third-party launchers from covering mobile catalog and
 * product-buying controls. Open or keyboard-focused widgets always remain visible.
 */
(function (factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') api.mount(window, document);
})(function () {
  'use strict';

  const MOBILE_MAX = 749;
  const COLLISION_GAP = 6;
  const STATE_ATTRIBUTE = 'data-cc-commerce-collision';
  const STATE_VALUE = 'suppressed';
  const SHADOW_STYLE_ATTRIBUTE = 'data-cc-commerce-widget-style';
  const PROTECTED_SELECTOR = [
    '.cc-gift-cover__words',
    '.cc-gift-contents__heading',
    '.cc-gift-contents__group',
    '.cc-gift-editorial__layout',
    '.cc-gift-chapter__heading',
    '.cc-gift-family-identity',
    '.cc-gift-chapter__photo',
    '.cc-gift-chapter__intro',
    '.cc-gift-budget-note',
    '.cc-gift-pick',
    '.cc-gift-note',
    '.cc-gift-chapter__footer',
    '.cc-gift-help__grid',
    '.cc-gift-download',
    '.cc-gift-signoff',
    '.cc-gift-invitation__inside',
    "main form[action*='/cart/add']",
    'main .product-form',
    'main .product-form__buttons',
    'main .product-form__input',
    'main variant-picker',
    'main .shopify-payment-button',
    'main shopify-accelerated-checkout',
    'main [data-product-form]',
    'main [data-add-to-cart]',
    "main [class*='product-option']",
    "main [class*='variant-picker']",
    "main [class*='personaliz']",
    "main [class*='engraving']"
  ].join(',');

  function rectsIntersect(first, second, gap) {
    const padding = Number(gap) || 0;
    return first.left < second.right + padding &&
      first.right > second.left - padding &&
      first.top < second.bottom + padding &&
      first.bottom > second.top - padding;
  }

  function shouldSuppress(state) {
    return Boolean(
      state.mobile &&
      state.visible &&
      state.intersects &&
      !state.expanded &&
      !state.focused
    );
  }

  function googleIsExpanded(wrapper, rect) {
    if (wrapper.getAttribute('aria-expanded') === 'true') return true;
    if (wrapper.querySelector("[aria-expanded='true']")) return true;
    const stateText = [
      wrapper.className || '',
      wrapper.getAttribute('data-state') || '',
      wrapper.getAttribute('data-view') || ''
    ].join(' ');
    if (/(^|\s)(open|opened|expanded)(\s|$)/i.test(stateText)) return true;
    // The collapsed trust badge may briefly render as a wide label. Height is
    // the reliable geometry signal for the expanded panel.
    return rect.height > 120;
  }

  function mount(win, doc) {
    if (win.__ccCommerceWidgetGuardInstalled) return win.__ccCommerceWidgetGuardInstalled;

    let frameRequested = false;
    let protectedDirty = true;
    let protectedElements = [];
    let observedChatRoot = null;
    let chatObserver = null;
    let googleObserver = null;
    let observedGoogle = null;
    const controlledElements = new Set();

    function isMobile() {
      return win.innerWidth <= MOBILE_MAX;
    }

    function isRenderableRect(rect) {
      return rect.width > 1 && rect.height > 1 &&
        rect.right > 0 && rect.bottom > 0 &&
        rect.left < win.innerWidth && rect.top < win.innerHeight;
    }

    function setSuppressed(element, suppressed) {
      if (!element) return;
      if (suppressed) {
        element.setAttribute(STATE_ATTRIBUTE, STATE_VALUE);
        controlledElements.add(element);
      } else if (controlledElements.has(element) || element.getAttribute(STATE_ATTRIBUTE) === STATE_VALUE) {
        element.removeAttribute(STATE_ATTRIBUTE);
        controlledElements.delete(element);
      }
    }

    function restoreAll() {
      controlledElements.forEach(function (element) {
        element.removeAttribute(STATE_ATTRIBUTE);
      });
      controlledElements.clear();
    }

    function getProtectedElements() {
      if (!protectedDirty) return protectedElements;
      protectedElements = Array.from(new Set(doc.querySelectorAll(PROTECTED_SELECTOR)));
      protectedDirty = false;
      return protectedElements;
    }

    function getProtectedRects() {
      return getProtectedElements().map(function (element) {
        return element.getBoundingClientRect();
      }).filter(isRenderableRect);
    }

    function intersectsProtected(rect, protectedRects) {
      return protectedRects.some(function (protectedRect) {
        return rectsIntersect(rect, protectedRect, COLLISION_GAP);
      });
    }

    function elementHasFocus(element, extraActiveElement) {
      if (!element) return false;
      const active = doc.activeElement;
      const extraIsFocused = Boolean(extraActiveElement) &&
        (element === extraActiveElement || element.contains(extraActiveElement));
      return element === active || element.contains(active) || extraIsFocused ||
        (typeof element.matches === 'function' && element.matches(':focus-within'));
    }

    function ensureChatShadowStyle(shadowRoot) {
      if (shadowRoot.querySelector('style[' + SHADOW_STYLE_ATTRIBUTE + ']')) return;
      const style = doc.createElement('style');
      style.setAttribute(SHADOW_STYLE_ATTRIBUTE, '');
      style.textContent = [
        '@media (max-width: ' + MOBILE_MAX + 'px) {',
        "  .activator-nudge[" + STATE_ATTRIBUTE + "='" + STATE_VALUE + "'],",
        "  button[part~='activator'][" + STATE_ATTRIBUTE + "='" + STATE_VALUE + "'] {",
        '    opacity: 0 !important;',
        '    pointer-events: none !important;',
        '    transition: none !important;',
        '  }',
        "  .activator-nudge[" + STATE_ATTRIBUTE + "='" + STATE_VALUE + "']:focus-within,",
        "  button[part~='activator'][" + STATE_ATTRIBUTE + "='" + STATE_VALUE + "']:focus {",
        '    opacity: 1 !important;',
        '    pointer-events: auto !important;',
        '  }',
        '}'
      ].join('\n');
      shadowRoot.appendChild(style);
    }

    function observeChatRoot(shadowRoot) {
      if (observedChatRoot === shadowRoot) return;
      if (chatObserver) chatObserver.disconnect();
      observedChatRoot = shadowRoot;
      ensureChatShadowStyle(shadowRoot);
      chatObserver = new win.MutationObserver(schedule);
      chatObserver.observe(shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-expanded', 'class', 'style', 'hidden']
      });
      shadowRoot.addEventListener('focusin', schedule, true);
      shadowRoot.addEventListener('focusout', schedule, true);
    }

    function getChatWidget() {
      const host = doc.querySelector("shopify-chat[view='chat'], shopify-chat");
      const shadowRoot = host && host.shadowRoot;
      if (!shadowRoot) return null;
      observeChatRoot(shadowRoot);
      const button = shadowRoot.querySelector("button[part~='activator'][aria-label='Chat'], button[part~='activator']");
      const target = button && (button.closest('.activator-nudge') || button);
      if (!button || !target) return null;
      return {
        target: target,
        rectElement: button,
        expanded: button.getAttribute('aria-expanded') === 'true',
        focused: elementHasFocus(target, shadowRoot.activeElement)
      };
    }

    function observeGoogleWidget(wrapper) {
      if (observedGoogle === wrapper) return;
      if (googleObserver) googleObserver.disconnect();
      observedGoogle = wrapper;
      googleObserver = new win.MutationObserver(schedule);
      googleObserver.observe(wrapper, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-expanded', 'class', 'style', 'hidden']
      });
    }

    function getGoogleWidget() {
      const wrapper = doc.getElementById('google-merchantwidget-iframe-wrapper');
      if (!wrapper) return null;
      observeGoogleWidget(wrapper);
      const iframe = wrapper.querySelector('iframe');
      const rect = wrapper.getBoundingClientRect();
      return {
        target: wrapper,
        rectElement: wrapper,
        rect: rect,
        expanded: googleIsExpanded(wrapper, rect),
        focused: elementHasFocus(wrapper, iframe && doc.activeElement === iframe ? iframe : null)
      };
    }

    function evaluateWidget(widget, protectedRects) {
      if (!widget) return;
      const rect = widget.rect || widget.rectElement.getBoundingClientRect();
      const state = {
        mobile: isMobile(),
        visible: isRenderableRect(rect),
        intersects: intersectsProtected(rect, protectedRects),
        expanded: widget.expanded,
        focused: widget.focused
      };
      setSuppressed(widget.target, shouldSuppress(state));
    }

    function evaluate() {
      frameRequested = false;
      controlledElements.forEach(function (element) {
        if (!element.isConnected) {
          element.removeAttribute(STATE_ATTRIBUTE);
          controlledElements.delete(element);
        }
      });
      if (!isMobile()) {
        restoreAll();
        return;
      }
      const protectedRects = getProtectedRects();
      if (!protectedRects.length) {
        restoreAll();
        return;
      }
      evaluateWidget(getChatWidget(), protectedRects);
      evaluateWidget(getGoogleWidget(), protectedRects);
    }

    function schedule() {
      if (frameRequested) return;
      frameRequested = true;
      win.requestAnimationFrame(evaluate);
    }

    const documentObserver = new win.MutationObserver(function () {
      protectedDirty = true;
      schedule();
    });

    function start() {
      if (!doc.body) return;
      documentObserver.observe(doc.body, { childList: true, subtree: true });
      win.addEventListener('scroll', schedule, { passive: true });
      win.addEventListener('resize', function () {
        protectedDirty = true;
        schedule();
      }, { passive: true });
      win.addEventListener('orientationchange', function () {
        protectedDirty = true;
        schedule();
      }, { passive: true });
      win.addEventListener('blur', schedule, true);
      doc.addEventListener('focusin', schedule, true);
      doc.addEventListener('focusout', schedule, true);
      win.addEventListener('pagehide', restoreAll, { once: true });
      schedule();
      [500, 1500, 3000, 6000].forEach(function (delay) {
        win.setTimeout(schedule, delay);
      });
    }

    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
    else start();

    const controller = { schedule: schedule, restore: restoreAll };
    win.__ccCommerceWidgetGuardInstalled = controller;
    return controller;
  }

  return {
    googleIsExpanded: googleIsExpanded,
    rectsIntersect: rectsIntersect,
    shouldSuppress: shouldSuppress,
    mount: mount
  };
});
